import { isEmpty } from 'lodash'
import ApiHelper from '../../helpers/ApiHelper'

export class ApiService {
    /// USERS
    async login(login) {
        return await ApiHelper.post(`/v1/users/login`, login)
    }

    async forgetPassword(data) {
        return await ApiHelper.post(`/v1/users/forgot-password`, data)
    }

    async validateForgotPasswordCode(data) {
        return await ApiHelper.post(`/v1/users/validate-forgot-password-code`, data)
    }

    async updatePassword(data) {
        return await ApiHelper.put(`/v1/users/update-password`, data)
    }

    async signup(user) {
        return await ApiHelper.post(`/v1/users/signup`, user)
    }

    async validateCode(data) {
        return await ApiHelper.post(`/v1/users/validate-code`, data)
    }

    async resendCode(data) {
        return await ApiHelper.post(`/v1/users/resend-code`, data)
    }

    async getPosition(data) {
        return await ApiHelper.post(`/v1/users/waiting-list`, data)
    }

    async getUserById(userId) {
        return await ApiHelper.get(`/v1/users/get-by-id/${userId}`)
    }

    async updateUser(user) {
        return await ApiHelper.put(`/v1/users/update`, user)
    }

    async uploadUserAvatar(data) {
        return await ApiHelper.postWithFiles(`/v1/users/upload-avatar`, data)
    }

    /// BRANDS
    async getBrands() {
        return await ApiHelper.get(`/v1/brand/get`)
    }

    async getBrandById(id) {
        return await ApiHelper.get(`/v1/brand/get-by-id/${id}`)
    }

    /// Family
    async getFamily() {
        return await ApiHelper.get(`/v1/family/get`)
    }

    async getFamilyById(id) {
        return await ApiHelper.get(`/v1/family/get-by-id/${id}`)
    }

    /// Category
    async getCategories() {
        return await ApiHelper.get(`/v1/category/get`)
    }

    async getCategoriesByFamilyId(id) {
        return await ApiHelper.get(`/v1/category/get-by-family/${id}`)
    }

    /// Subcategory
    async getSubcategories() {
        return await ApiHelper.get(`/v1/subcategory/get`)
    }

    async getSubcategoriesByCategoryId(id) {
        return await ApiHelper.get(`/v1/subcategory/get-by-category/${id}`)
    }

    /// PRODUCTS
    async getProducts() {
        return await ApiHelper.get(`/v1/products/get`)
    }

    async getProductById(id, includeVariants) {
        const query = includeVariants ? `?includeVariants=true` : ''
        return await ApiHelper.get(`/v1/products/get-by-id/${id}${query}`)
    }

    async getProductBySlug(slug) {
        return await ApiHelper.get(`/v1/products/get-by-slug/${slug}`)
    }

    async getProductBySupplierAndCode(supplierId, code, includeVariants) {
        const query = includeVariants ? `?includeVariants=true` : ''
        return await ApiHelper.get(`/v1/products/get-by-supplier-and-code/${supplierId}/${code}${query}`)
    }

    async getProductsBySupplierAndFilter(supplierId, filter) {
        return await ApiHelper.get(`/v1/products/get-all-by-supplier-and-filter/${supplierId}/${filter}`)
    }

    async getProductsByKeyword(keyword) {
        return await ApiHelper.get(`/v1/products/search/${keyword}`)
    }

    async getProductVariant(id) {
        return await ApiHelper.get(`/v1/products/variants/get/${id}`)
    }

    async getProductsBySlug(
        slug,
        offset,
        limit,
        filterFamilies = [],
        filterCategories = [],
        filterSubcategories = [],
        filterPrice = []
    ) {
        const query = `offset=${offset}&limit=${limit}&families=${filterFamilies}&categories=${filterCategories}&subcategories=${filterSubcategories}&price=${filterPrice}`
        return await ApiHelper.get(
            `/v1/catalogs/get-products-by-slug/${slug}?${query}`
        )
    }

    async getFamiliesBySlug(slug) {
        return await ApiHelper.get(`/v1/catalogs/get-families-by-slug/${slug}`)
    }

    async getRangePriceBySlug(slug) {
        return await ApiHelper.get(`/v1/catalogs/get-range-price-by-slug/${slug}`)
    }

    async getCategoriesBySlug(slug, filterFamilies) {
        return await ApiHelper.get(`/v1/catalogs/get-categories-by-slug/${slug}?families=${filterFamilies}`)
    }

    async getSubcategoriesBySlug(slug, filterFamilies, filterCategories) {
        return await ApiHelper.get(`/v1/catalogs/get-subcategories-by-slug/${slug}?families=${filterFamilies}&categories=${filterCategories}`)
    }

    // Module Brands

    async getProductsBySupplierCode(
        supplierCode,
        offset,
        limit,
        filterFamilies = [],
        filterCategories = [],
        filterSubcategories = [],
        filterPrice = [],
        searcher = '',
    ) {
        const query = `offset=${offset}&limit=${limit}&families=${filterFamilies}&categories=${filterCategories}&subcategories=${filterSubcategories}&price=${filterPrice}&search=${searcher}`
        return await ApiHelper.get(
            `/v1/suppliers/get-products-by-code/${supplierCode}?${query}`
        )
    }

    async getFamiliesBySupplierCode(supplierCode) {
        return await ApiHelper.get(`/v1/suppliers/get-families-by-code/${supplierCode}`)
    }

    async getRangePriceBySupplierCode(supplierCode) {
        return await ApiHelper.get(`/v1/suppliers/get-range-price-by-code/${supplierCode}`)
    }

    async getCategoriesBySupplierCode(supplierCode, filterFamilies) {
        return await ApiHelper.get(`/v1/suppliers/get-categories-by-code/${supplierCode}?families=${filterFamilies}`)
    }

    async getSubcategoriesBySupplierCode(supplierCode, filterFamilies, filterCategories) {
        return await ApiHelper.get(`/v1/suppliers/get-subcategories-by-code/${supplierCode}?families=${filterFamilies}&categories=${filterCategories}`)
    }

    // Discovery
    async getProductsFamilies() {
        return await ApiHelper.get(`/v1/products/get-families`)
    }

    async getProductsRangePrice() {
        return await ApiHelper.get(`/v1/products/get-range-price`)
    }

    async getProductsCategories(filterFamilies) {
        return await ApiHelper.get(`/v1/products/get-categories?families=${filterFamilies}`)
    }

    async getProductsSubcategories(filterFamilies, filterCategories) {
        return await ApiHelper.get(`/v1/products/get-subcategories?families=${filterFamilies}&categories=${filterCategories}`)
    }

    async getProductsSuppliers() {
        return await ApiHelper.get(`/v1/products/get-suppliers`)
    }

    async getProductsBrands() {
        return await ApiHelper.get(`/v1/products/get-brands`)
    }

    async getProductsDiscovery(
        offset,
        limit,
        filterFamilies = [],
        filterCategories = [],
        filterSubcategories = [],
        filterPrice = [],
        filterSuppliers = [],
        searcher = '',
    ) {
        const query = `offset=${offset}&limit=${limit}&families=${filterFamilies}&categories=${filterCategories}&subcategories=${filterSubcategories}&price=${filterPrice}&suppliers=${filterSuppliers}&search=${searcher}`
        return await ApiHelper.get(
            `/v1/products/get-products?${query}`
        )
    }

    async getProductsMostSold() {
        return await ApiHelper.get(`/v1/products/get-most-sold`)
    }

    // Catalogs
    async getCatalogs() {
        return await ApiHelper.get('/v1/catalogs/get')
    }

    async getCatalogById(id) {
        return await ApiHelper.get(`/v1/catalogs/get-by-id/${id}`)
    }

    async getCatalogBySlug(slug, authToken) {
        return await ApiHelper.get(`/v1/catalogs/get-by-slug/${slug}${authToken}`)
    }

    async getCatalogsBySupplier(supplierId) {
        return await ApiHelper.get(`/v1/catalogs/get-by-supplier/${supplierId}`)
    }

    async shareCatalogByEmail(data, authToken) {
        return await ApiHelper.post(`/v1/catalogs/share-by-email?authToken=${authToken}`, data)
    }

    async getOrdersByUser(offset, limit, filter) {
        let moreParams = '&'
        if (!isEmpty(filter)) {
            for (const f of filter) {
                moreParams += `${f.columnName}=${f.value}&`
            }
        }
        return await ApiHelper.get(`/v1/orders/get-by-user?offset=${offset}&limit=${limit}${moreParams}`)
    }

    async getDetailOfOrderByUser(id) {
        return await ApiHelper.get(`/v1/orders/get-by-user/order/${id}`)
    }

    async createOrder(data) {
        return await ApiHelper.post(`/v1/orders/save`, data)
    }

    async updateOrder(data) {
        return await ApiHelper.put(`/v1/orders/update`, data)
    }

    async deleteOrder(id) {
        return await ApiHelper.delete(`/v1/orders/delete/${id}`)
    }

    // Suppliers
    async getSuppliers() {
        return await ApiHelper.get('/v1/suppliers/get')
    }

    async getSuppliersForOrder() {
        return await ApiHelper.get('/v1/suppliers/get-for-order')
    }

    // CART
    async getCart() {
        return await ApiHelper.get(`/v1/cart/get`)
    }

    async getCartCountItems() {
        return await ApiHelper.get(`/v1/cart/count-items`)
    }

    async addToCart(data) {
        return await ApiHelper.post(`/v1/cart/save`, data)
    }

    async deleteCartItem(cartItemId) {
        return await ApiHelper.delete(`/v1/cart/delete-cart-item/${cartItemId}`)
    }

    async updateCartItemQuantity(cartItemId, quantity) {
        return await ApiHelper.put(`/v1/cart/update-cart-item-quantity/${cartItemId}`, quantity)
    }

    async resetCart() {
        return await ApiHelper.put(`/v1/cart/reset`, {})
    }

    async getUserAddress(offset, limit, filter) {
        let moreParams = '&'
        if (!isEmpty(filter)) {
            for (const f of filter) {
                moreParams += `${f.columnName}=${f.value}&`
            }
        }
        return await ApiHelper.get(`/v1/user-address/get?offset=${offset}&limit=${limit}${moreParams}`)
    }

    async getMyAddress() {
        return await ApiHelper.get(`/v1/user-address/get`)
    }

    async getAllUserAddress() {
        return await ApiHelper.get(`/v1/user-address/get-all`)
    }

    async getUserDefaultAddress() {
        return await ApiHelper.get(`/v1/user-address/get-default`)
    }

    async setUserDefaultAddress(data) {
        return await ApiHelper.put(`/v1/user-address/set-default`, data)
    }

    async getCountries(code) {
        return await ApiHelper.get(`/v1/countries/get?code=${code}`)
    }

    async getAllCountries() {
        return await ApiHelper.get(`/v1/countries/get`)
    }

    async getStatesByCountry(countryId) {
        return await ApiHelper.get(`/v1/states/get-by-country/${countryId}`)
    }

    async createUserAddress(data) {
        return await ApiHelper.post(`/v1/user-address/save`, data)
    }

    async updateUserAddress(data) {
        return await ApiHelper.put(`/v1/user-address/update`, data)
    }

    async getUserAddressById(id) {
        return await ApiHelper.get(`/v1/user-address/get-by-id/${id}`)
    }

    async deleteUserAddress(id) {
        return await ApiHelper.delete(`/v1/user-address/delete/${id}`)
    }

    async createIntentOfPayment(data) {
        return await ApiHelper.post(`/v1/orders/payment-intent`, data)
    }

    async markeOrderAsPaid(data) {
        return await ApiHelper.put(`/v1/orders/mark-as-paid`, data)
    }

    async getStoreByUser() {
        return await ApiHelper.get(`/v1/store/get`)
    }

    async checkStoreUrl(url) {
        return await ApiHelper.post(`/v1/store/check-url`, { url })
    }

    async getStoreInit() {
        return await ApiHelper.get(`/v1/store/init`)
    }

    async getStoreInitProducts() {
        return await ApiHelper.get(`/v1/store/init-products`)
    }

    async createStore(data) {
        return await ApiHelper.post(`/v1/store/create`, data)
    }

    async getStoreById(id) {
        return await ApiHelper.get(`/v1/store/get/${id}`)
    }

    async addProductsToStore(data) {
        return await ApiHelper.post(`/v1/store/add-products`, data)
    }

    async updateStore(data) {
        return await ApiHelper.put(`/v1/store/update`, data)
    }

    async removeProductOfStore(id) {
        return await ApiHelper.delete(`/v1/store/delete-detail/${id}`)
    }

    async addProductToStore(data) {
        return await ApiHelper.put(`/v1/store/add-product`, data)
    }

    async getAvatars() {
        return await ApiHelper.get(`/v1/avatars/get`)
    }

    async updateAvatar(data) {
        return await ApiHelper.put(`/v1/users/update-avatar`, data)
    }

    getUrlBaseStorage() {
        return 'https://storage.googleapis.com/vendora-storage/'
    }

    async checkCouponDiscount(data) {
        return await ApiHelper.post(`/v1/coupons-released/check-by-code`, data)
    }

    async getBanners() {
        return await ApiHelper.get(`/v1/banners/get`)
    }

    async getAcademys() {
        return await ApiHelper.get(`/v1/academy/get`)
    }
}

export default new ApiService()
